import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'
import {
  ReportMember,
  MemberCondition,
  DailySearchCondition,
  MemberReportCategoryWork,
  MemberReportApplicationProportion,
  ApplicationWorkTime,
  ApplicationTimeLine,
  Actions,
  Scheduler,
} from '../../types/member'

import {
  UserData,
  DataExists,
} from '../../types/report'

import {
  getReportMember,
  getReportExists,
  getUserData,
  getCategoryWork,
  getApplicationProportion,
  getApplicationWorkTime,
  getTimeLine,
} from '../../apis/memberReport'
import { getActions, getScheduler } from '../../apis/timeline'
import { GetTimeLineParams, getActionsResponse, getSchedulerResponse } from '../../types/timeline'

interface MemberReportState {
  isMemberLoading: boolean
  isDataExistsLoading: boolean
  isUserDataLoading: boolean
  isCategoryWorkLoading: boolean
  isApplicationProportion: boolean
  isApplicationWorkTime: boolean
  isApplicationTimeLine: boolean
  member: ReportMember
  evaluationExists: boolean
  userProcessExists: boolean
  isDisApprove: boolean | undefined
  userData: UserData
  categoryWork: MemberReportCategoryWork
  applicationProportion: MemberReportApplicationProportion
  applicationWorkTime: ApplicationWorkTime[]
  applicationTimeLine: ApplicationTimeLine[]
  timelineActions?: Actions
  timelineScheduler?: Scheduler
  timelineMaxCount?: number
}

const initialState: Partial<MemberReportState> = {
  evaluationExists: undefined,
  userProcessExists: undefined,
  isDisApprove: undefined,
  isMemberLoading: true,
  isDataExistsLoading: true,
  isUserDataLoading: true,
  isCategoryWorkLoading: true,
  isApplicationProportion: true,
  isApplicationWorkTime: true,
  isApplicationTimeLine: true,
}

const memberReportSlice = createSlice({
  name: 'memberReport',
  initialState,
  reducers: {
    requestMemberStart: (state) => {
      state.isMemberLoading = true
      return state
    },
    requestDataExistsStart: (state) => {
      state.isDataExistsLoading = true
      return state
    },
    requestUserDataStart: (state) => {
      state.isUserDataLoading = true
      return state
    },
    requestCategoryWork: (state) => {
      state.isCategoryWorkLoading = true
      return state
    },
    requestApplicationProportion: (state) => {
      state.isApplicationProportion = true
      return state
    },
    requestApplicationWorkTime: (state) => {
      state.isApplicationWorkTime = true
      return state
    },
    requestApplicationTimeLine: (state) => {
      state.isApplicationTimeLine = true
      return state
    },
    buildMemberSuccess: (state, action: PayloadAction<ReportMember>) => {
      state.isMemberLoading = false
      state.member = action.payload
      return state
    },
    buildMemberReportExists: (state, action: PayloadAction<DataExists>) => {
      state.isDataExistsLoading = false
      state.evaluationExists = action.payload.evaluation_exists
      state.userProcessExists = action.payload.user_process_exists
      return state
    },
    buildMemberReportUserDataSuccess: (state, action: PayloadAction<UserData>) => {
      state.isUserDataLoading = false
      state.userData = action.payload
      return state
    },
    buildMemberReportCategoryWork: (state, action: PayloadAction<MemberReportCategoryWork>) => {
      state.isCategoryWorkLoading = false
      state.categoryWork = action.payload
      return state
    },
    buildMemberReportApplicationProportion: (state, action: PayloadAction<MemberReportApplicationProportion>) => {
      state.isApplicationProportion = false
      state.applicationProportion = action.payload
      return state
    },
    buildMemberReportApplicationWorkTime: (state, action: PayloadAction<ApplicationWorkTime[]>) => {
      state.isApplicationWorkTime = false
      state.applicationWorkTime = action.payload
      return state
    },
    buildMemberReportApplicationTimeline: (state, action: PayloadAction<ApplicationTimeLine[]>) => {
      state.isApplicationTimeLine = false
      state.applicationTimeLine = action.payload
      return state
    },
    buildMemberTimelineActions: (state, action: PayloadAction<getActionsResponse>) => {
      state.timelineActions = action.payload.actions
      state.timelineMaxCount = action.payload.max_count
      return state
    },
    buildMemberTimelineScheduler: (state, action: PayloadAction<getSchedulerResponse>) => {
      state.timelineScheduler = action.payload.scheduler
      return state
    },
    setMemberReportApproval: (state) => {
      state.isDataExistsLoading = false
      state.isDisApprove = false
      return state
    },
    setMemberReportDisApproval: (state) => {
      state.isDataExistsLoading = false
      state.evaluationExists = false
      state.userProcessExists = false
      state.isDisApprove = true
      return state
    },
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestMemberStart,
  requestDataExistsStart,
  requestUserDataStart,
  requestCategoryWork,
  requestApplicationProportion,
  requestApplicationWorkTime,
  requestApplicationTimeLine,
  buildMemberSuccess,
  buildMemberReportExists,
  buildMemberReportUserDataSuccess,
  buildMemberReportCategoryWork,
  buildMemberReportApplicationProportion,
  buildMemberReportApplicationWorkTime,
  buildMemberReportApplicationTimeline,
  buildMemberTimelineActions,
  buildMemberTimelineScheduler,
  setMemberReportApproval,
  setMemberReportDisApproval,
  resetState,
} = memberReportSlice.actions

export const memberReportReducer = memberReportSlice.reducer

export const fetchMemberReportExists = (
  params: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestDataExistsStart())
  getReportExists(params)
    .then((result) => {
      dispatch(setMemberReportApproval())
      dispatch(buildMemberReportExists(result))
    })
    .catch((error) => {
      switch (error.status) {
        case 403:
          dispatch(setMemberReportDisApproval())
          break
        case 409:
          dispatch(setMemberReportDisApproval())
          break
        default:
          dispatch(setErrors(error))
          break
      }
    })
}

export const fetchReportUserData = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestUserDataStart())
  getUserData(param)
    .then((result: UserData) => {
      dispatch(buildMemberReportUserDataSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportCategoryWork = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestCategoryWork())
  getCategoryWork(param)
    .then((result: MemberReportCategoryWork) => {
      dispatch(buildMemberReportCategoryWork(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationProportion = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestApplicationProportion())
  getApplicationProportion(param)
    .then((result: MemberReportApplicationProportion) => {
      dispatch(buildMemberReportApplicationProportion(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationWorkTime = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestApplicationWorkTime())
  getApplicationWorkTime(param)
    .then((result: ApplicationWorkTime[]) => {
      dispatch(buildMemberReportApplicationWorkTime(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationTimeLine = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestApplicationTimeLine())
  getTimeLine(param)
    .then((result: ApplicationTimeLine[]) => {
      dispatch(buildMemberReportApplicationTimeline(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportMember = (
  param: MemberCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestMemberStart())
  getReportMember(param)
    .then((result: ReportMember) => {
      dispatch(buildMemberSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchMemberTimelineActions = (
  param: GetTimeLineParams,
): AppThunk => async (dispatch) => {
  getActions(param)
    .then((result) => {
      dispatch(buildMemberTimelineActions(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchMemberTimelineScheduler = (
  param: GetTimeLineParams,
): AppThunk => async (dispatch) => {
  getScheduler(param)
    .then((result) => {
      dispatch(buildMemberTimelineScheduler(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}
